import { get } from 'svelte/store';
//Formatters
import { format } from 'svelte-i18n';
import { toast } from '@Shelly/shelly-design-system';
import { gen2CloudReq, gen2Request } from 'src/services/gen_2_http_reqs';
import { celsius_to_fahrenheit, isBluTRVGateway, real_id } from '../interface_helpers';
import {
	createVirtualDevice,
	deleteVirtualDevice,
	getComponentByDevice,
	getDeviceIDByComponent,
} from './virtual_components_helpers';
import { updating_devices } from 'src/stores/device-runtime/process';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { getBatteryIconStatus } from './virtual_icons_helpers';
import { onSettingsResponse } from '../device_settings_helpers';
import { getVComponentStatus } from './virtual_components_status_helpers';
import { sortOn } from '../utilities';
import { compareFWVersions } from '../firmware_compare_helpers';
import { getTRVFirmwareVersion } from '../firmware_helpers';
import { bthomeObjectInfos } from './virtual_bthome_helper';
//Types
import type { Device } from 'src/types/Device';
import type {
	BluTRVInterfaceData,
	TRVComponentError,
	TRVComponentInterface,
} from 'src/types/TRVComponent';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type {
	VirtualComponentInterface,
	VirtualComponentInterfaceData,
} from 'src/types/VirtualComponents';

export const callBluTRV = async (
	device: Device,
	method: string,
	params: any,
	showToastOnResponse = true,
	viaq: boolean = true,
): Promise<SettingRequest> => {
	const id = getTrvComponentByDevice(device)?.id;
	const idToSend = device.shared ? device.id : real_id(device.id); //if the device is shared we need to send the real id because that is how it is stored in the devices store

	if (!id) return { isok: false } as SettingRequest;

	const res: SettingRequest = await gen2CloudReq(
		idToSend,
		device.type,
		'blutrv.call',
		{
			id,
			method,
			params,
		},
		viaq,
	);
	if (showToastOnResponse || res?.errors) {
		if (res?.isok) {
			toast.open({
				type: 'success',
				title: get(format)('command_is_sent_to_the_device', {
					default: 'The command is sent to the device.',
				}),
			});
		} else {
			onSettingsResponse(res, device);
		}
	}
	return res;
};

export const deleteBluTRV = async (device: Device): Promise<SettingRequest> => {
	const id = getTrvComponentByDevice(device)?.id;
	if (!id) return (await deleteVirtualDevice(device)) as SettingRequest;
	const res: SettingRequest = await gen2Request(device, 'blutrv.delete', {
		id,
	});
	if (res?.isok) {
		toast.open({
			type: 'success',
			title: get(format)(`type_del_scc_toast`, {
				values: { this_type: get(format)(`process_delete_device`) },
			}),
		});
	} else {
		onSettingsResponse(res, device);
	}
	return res;
};

export const getTrvComponentByDevice = (device: Device): TRVComponentInterface => {
	const id = real_id(device.id);
	const deviceSettings: DeviceSettingsG2 = devices_settings.getByRealId(id);
	const component = getComponentByDevice(device);
	const trvConfig = deviceSettings?.[component];
	if (!trvConfig) return null;
	const deviceStatus: DeviceStatusG2 = devices_status.getByRealId(device.id);
	const trvStatus = deviceStatus?.[component];
	const trvID = trvConfig?.id;
	const trvBluConfig = deviceSettings?.[`blutrv_rconfig:${trvID}`]?.config;
	const trvBluStatus = deviceStatus?.[`blutrv_rstatus:${trvID}`]?.status;
	const fwUpdating = get(updating_devices)?.[device.id];
	if (fwUpdating && Date.now() > fwUpdating?.ts + 1 * 60 * 3000) {
		updating_devices.remove(device.id);
	}
	return {
		id: trvID,
		component,
		config: { ...trvConfig, ...trvBluConfig },
		status: { ...trvStatus, ...trvBluStatus },
		schedules: deviceSettings?.[`blutrv_rsched:${trvID}`],
		device_info: deviceStatus?.[`blutrv_rinfo:${trvID}`]?.device_info,
	};
};

export const isBluTRVCalibrated = (device: Device): boolean => {
	return !getBluTRVError(device, 'not_calibrated');
};

export const isBluTRVMounted = (device: Device): boolean => {
	return !getBluTRVError(device, 'not_mounted');
};

export const isBluTRVExtTempMissing = (device: Device): boolean => {
	if (compareFWVersions(getTRVFirmwareVersion(device), '<=', '1.2.1')) {
		return false;
	}
	const trvComponent: TRVComponentInterface = getTrvComponentByDevice(device);
	return (
		getBluTRVError(device, 'ext_temp_missing') &&
		trvComponent?.config?.temp_sensors?.length > 0 &&
		trvComponent?.config?.['trv:0']?.flags &&
		trvComponent.config['trv:0'].flags.includes('floor_heating')
	);
};

const getBluTRVError = (device: Device, error: TRVComponentError): boolean | undefined => {
	const trvStatus: TRVComponentInterface['status']['trv:0'] =
		getTrvComponentByDevice(device)?.status?.['trv:0'];
	return trvStatus?.errors && trvStatus.errors.includes(error);
};

export const getBluTRVStatus = (device: Device): BluTRVInterfaceData[] => {
	const interface_data = [];
	const trvComponent = getTrvComponentByDevice(device);
	const enableTrv = trvComponent?.config?.['trv:0']?.enable;
	const unit = device?.ht_ui?.temperature_unit || 'C';
	const trvStatus: TRVComponentInterface['status']['trv:0'] = trvComponent?.status?.['trv:0'];
	if (!trvStatus) return interface_data;

	if (trvStatus.errors.includes('connect_failed')) {
		interface_data.push({
			name: 'connect_failed',
			value: get(format)('label_connect_failed', {
				default: 'Connect failed',
			}),
			icon: 'fas fa-triangle-exclamation',
			iconColor: '--color-warning',
		});
	}

	if (trvStatus.boost) {
		interface_data.push({
			name: 'boost time',
			value: null,
			icon: 'far fa-fire',
			iconColor: '--color-primary',
		});
	}

	const currTemperatureInC = trvStatus[`current_C`].toFixed(1);
	const currTemperatureInF = celsius_to_fahrenheit(trvStatus[`current_C`]);
	if (currTemperatureInC != null) {
		interface_data.push({
			name: 'current_temperature',
			value: unit === 'C' ? `${currTemperatureInC} °C` : `${currTemperatureInF} °F`,
			event: unit === 'C' ? currTemperatureInC : currTemperatureInF,
			icon: 'far fa-thermometer-three-quarters',
			unit: `°${unit}`,
		});
	}

	let targetTemperatureInC = trvStatus[`target_C`].toFixed(1);
	let targetTemperatureInF = celsius_to_fahrenheit(trvStatus[`target_C`]);
	if (targetTemperatureInC != null && enableTrv) {
		interface_data.push({
			name: 'target_temperature',
			value: unit === 'C' ? `${targetTemperatureInC} °C` : `${targetTemperatureInF} °F`,
			event: unit === 'C' ? targetTemperatureInC : targetTemperatureInF,
			icon: 'far fa-temperature-sun',
			unit: `°${unit}`,
		});
	}

	const valvePosition = trvStatus?.pos;
	if (valvePosition != null) {
		interface_data.push({
			name: 'valve position',
			value: `${valvePosition} %`,
			event: valvePosition,
			icon: `fa${valvePosition > 0 ? 's' : 'r'} fa-pipe-valve`,
			unit: '%',
		});
	}

	const battery = trvComponent?.status?.battery;
	if (battery != null) {
		const batteryIcon = getBatteryIconStatus(battery);
		interface_data.push({
			name: 'battery',
			value: `${battery} %`,
			icon: batteryIcon.icon,
			...(batteryIcon.iconStyle && { iconStyle: batteryIcon.iconStyle }),
			...(batteryIcon.iconColor && { iconColor: batteryIcon.iconColor }),
		});
	}

	return interface_data;
};

export const getSensorsByBTHomeDevice = (
	device: Device,
	bthomeSensor: string,
): {
	name: string;
	list: VirtualComponentInterfaceData[];
	addr: string;
	bthomeDevice: string;
} => {
	const id = real_id(device.id);
	const deviceSettings: DeviceSettingsG2 = devices_settings.getByRealId(id);
	const sensorComponent = deviceSettings?.[bthomeSensor];
	let name: string = '';
	let bthomeDevice: string = '';
	const list: VirtualComponentInterfaceData[] = [];
	if (sensorComponent) {
		for (const component in deviceSettings) {
			const componentSetting = deviceSettings[component];
			if (
				/^(bthomedevice|bthomesensor):2\d{2}$/.test(component) &&
				componentSetting?.addr == sensorComponent?.addr
			) {
				if (/^bthomedevice:2\d{2}$/.test(component) && componentSetting?.obj_id != 58) {
					name = componentSetting.name || component;
					bthomeDevice = component;
				} else {
					const item: VirtualComponentInterfaceData = getVComponentStatus(device, [
						component,
					])[0];
					const excludeSensors = ['battery_status', 'light', 'button'];
					const shouldExclude = bthomeObjectInfos.some(
						(i) => i.id === componentSetting?.obj_id && excludeSensors.includes(i.name),
					);
					if (item && !shouldExclude) {
						list.push(item);
					}
				}
			}
		}
		if (list.length == 0) {
			list.push({
				icon: 'fa-sharp far fa-hourglass-clock',
				value: get(format)('waiting_for_data', {
					default: 'Waiting for data',
				}),
			});
		}
		return {
			name,
			list: sortOn(list, 'id', true),
			bthomeDevice,
			addr: sensorComponent?.addr,
		};
	}
	return null;
};

export const syncBluTRVDevicesByParent = async (
	deviceID: Device['id'],
	deviceSettings: DeviceSettingsG2,
) => {
	const allDevices = get(devices);
	if (!allDevices[deviceID]) return; // Skip the check for shared devices.
	const trvComponents = Object.keys(deviceSettings || {}).filter((i) => /^(blutrv):2\d{2}$/.test(i));
	for (const trv in trvComponents) {
		const trvComponent = trvComponents[trv];
		const trvDeviceID = getDeviceIDByComponent(deviceID, trvComponent);
		const trvDevice: Device = allDevices[trvDeviceID];
		if (!trvDevice) {
			const config: VirtualComponentInterface['config'] = deviceSettings[trvComponent];
			await createVirtualDevice(allDevices[deviceID], config, trvComponent);
		}
	}
	for (const dev in allDevices) {
		if (new RegExp(`${deviceID}_22\\d{2}$`).test(dev)) {
			const trvComponent = `blutrv:${dev.split('_2')[1]}`;
			if (
				//Check if the blutrv:2xx component is missing from the settings for the GW G3
				!trvComponents.includes(trvComponent) &&
				//Check if the blutrv:2xx is missing from status
				!checkTRVStatusComponent(deviceID, trvComponent)
			) {
				//Verify if the TRV device's MAC address exists in the GW3 settings under components
				// as a bthomedevice component with the same MAC address.
				const device: Device = allDevices[dev];
				if (!getBthomeComponentByDeviceMac(device?.addr, deviceSettings)) {
					await deleteVirtualDevice(device);
				}
			}
		}
	}
};

const checkTRVStatusComponent = (deviceID: string, trvComponent: string): boolean => {
	const trvStatus = devices_status.getByRealId(deviceID);
	return trvStatus?.vcomps?.includes && trvStatus.vcomps.includes(trvComponent);
};

const getBthomeComponentByDeviceMac = (
	deviceMac: Device['addr'],
	deviceSettings: DeviceSettingsG2,
): string => {
	const bthomeDeviceComponent =
		deviceMac &&
		Object.keys(deviceSettings).find(
			(i) => /^(bthomedevice):2\d{2}$/.test(i) && deviceSettings[i].addr == deviceMac,
		);
	return bthomeDeviceComponent;
};

devices_settings.subscribe((settings) => {
	for (const dev in settings) {
		if (isBluTRVGateway(settings[dev]?.device?.type)) {
			syncBluTRVDevicesByParent(dev, settings[dev]);
		}
	}
});

export const getBleSignalStrength = (rssi: number): string => {
	const $format = get(format);
	if (typeof rssi !== 'number') {
		return $format('status_unknown', { default: 'Unknown' });
	}

	if (rssi > -55) {
		return $format('signal_val_vg', { default: 'Very Good' });
	}
	if (rssi > -70) {
		return $format('signal_val_g', { default: 'Good' });
	}
	if (rssi > -80) {
		return $format('signal_val_fair', { default: 'Fair' });
	}
	if (rssi > -85) {
		return $format('signal_val_weak', { default: 'Weak' });
	}
	if (rssi > -90) {
		return $format('signal_val_no_signal', { default: 'No signal' });
	}

	return $format('signal_val_no_signal', { default: 'No signal' });
};
